import { useEffect } from "react";
import { UseAuthReturn } from "@clerk/types";
import Axios, { AxiosError, AxiosRequestConfig, isAxiosError } from "axios";
import qs from "qs";

import { env } from "@/env";

export const axiosInstance = Axios.create({
  baseURL: new URL("/api/v1", env.VITE_API_URL).toString(),
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: "comma" });
  },
  params: {},
});

// To make Authorization, we need to use the useInitializeAuthorizationHeaderInterceptor as early as possible in react land (in App.tsx)
// all that is necessary to avoid getApi function from being useApi function
// this makes it possible to get fetcher functions and use them in `beforeLoad` or `loader` function of @tanstack-router
export const getApi = async <TResponse, TData = unknown>(
  config: AxiosRequestConfig<TData>,
  /**
   * @param options - can be used to alter request settings calling useGetSomethingById hook
   * This is rather escape hatch, but could be usefull when OpenAPI schema is not precise enough
   * one usecase was to remove default "Content-Type" header and allow browser to set it when uploading files
   * @example useGetSomethingById({ id: 1 }, { request: { headers: { "Content-Type": undefined } } })
   */
  options?: AxiosRequestConfig<TData>,
): Promise<TResponse> => {
  try {
    const promise = await axiosInstance<TResponse>({
      ...config,
      ...options,
      headers: {
        ...config.headers,
        ...options?.headers,
      },
    });

    return promise.data;
  } catch (error) {
    // AxiosError is an error returned external API
    // rethrow it so that it will be handled by onError in ReactQueryProvider for useQuery calls
    // or custom onError defined when calling useMutation
    if (isAxiosError<TResponse, TData>(error)) {
      throw error;
    } else {
      // if it's not an AxiosError, it's an error thrown by the client
      // likely issue with useApi configuration, getting token or else
      throw error;
    }
  }
};

export default getApi;

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

// In case you want to wrap the body type (optional)
// (if the custom instance is processing data before sending it, like changing the case for example)
export type BodyType<BodyData> = BodyData;

export const useInitializeAuthorizationHeaderInterceptor = (
  getToken: UseAuthReturn["getToken"],
) => {
  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.request.use(
      async (config) => {
        const token = await getToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          config.headers.Authorization = undefined;
        }
        return config;
      },
    );

    return () => {
      axiosInstance.interceptors.request.eject(interceptorId);
    };
  }, [getToken]);
};
